/**
 * Pure helpers for the Studio chat dock — thread ids, dock mode, model
 * choice, money formatting.
 *
 * DOM-free by construction (the persistence wrappers guard `localStorage`
 * themselves), so the contracts below are unit-tested in node alongside the
 * server suite — see chatThreads.test.ts.
 *
 * Why "default" is special: every chat row written before threads existed
 * carries no `threadId`, and the server reads those as `"default"`. So the
 * FIRST thread of a clip must BE "default" — otherwise a clip's existing
 * conversation would look like it vanished the moment the dock shipped.
 */

/** The thread every pre-threads row belongs to (mirrors src/lib/studioChat.ts). */
export const DEFAULT_THREAD_ID = "default";

/** What the server accepts as a thread id. */
export const THREAD_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

/** Dock placement: a 360 px column left of the canvas, or the floating
 *  right-hand drawer the Studio shipped with. */
export type ChatDockMode = "left" | "drawer";

/** Below this the three-column layout squeezes the canvas too hard, so a
 *  fresh visitor gets the drawer instead. */
export const DOCK_LEFT_MIN_WIDTH = 1100;

/** chat 360 + inspector 360 + gaps leaves a usable preview only above this;
 *  on a 1280 laptop the docked chat opens as its RAIL instead, so the first
 *  thing the operator sees is his video, not a 184 px thumbnail. One click
 *  opens it, and that choice is then remembered. */
export const DOCK_EXPANDED_MIN_WIDTH = 1400;

const LS_DOCK = "ql-studio-chat-dock";
const LS_COLLAPSED = "ql-studio-chat-collapsed";
const LS_MODEL = "ql-studio-chat-model";
const LS_THREAD_PREFIX = "ql-studio-chat-thread:";

/** A fresh client-generated thread id. `crypto.randomUUID` needs a secure
 *  context; the fallback is a time+random id that still matches THREAD_ID_RE. */
export function newThreadId(): string {
  try {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
  } catch {
    /* fall through to the time-based id */
  }
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

/** Dock mode for a viewport that has never been told otherwise. */
export function defaultDockMode(width: number): ChatDockMode {
  return width >= DOCK_LEFT_MIN_WIDTH ? "left" : "drawer";
}

/** Collapsed-by-default only where an expanded dock would cost the preview
 *  more than the chat is worth. */
export function defaultDockCollapsed(width: number): boolean {
  return width < DOCK_EXPANDED_MIN_WIDTH;
}

/**
 * Where the dock OPENS: what was stored, unless the window is too narrow to
 * hold it.
 *
 * Below `DOCK_EXPANDED_MIN_WIDTH` the stored choice is IGNORED and the chat
 * opens as the rail — always. The stored "expanded" was made on a wide
 * monitor, and honouring it on a 1280 laptop is what left the operator a
 * 266 px preview (QA 2026-09-16, B2): the exact failure §10.6 was written to
 * prevent. Opening it by hand still works for the visit; it is simply not
 * written down (`dockCollapsedIsPersistable`), so the narrow session can
 * never teach the wide one a preference it did not express.
 */
export function resolveDockCollapsed(stored: string | null, width: number): boolean {
  if (defaultDockCollapsed(width)) return true;
  return stored === "1";
}

/** Only a window wide enough to HOLD the expanded dock may record a choice
 *  about it. */
export function dockCollapsedIsPersistable(width: number): boolean {
  return !defaultDockCollapsed(width);
}

/** Stored value → mode, falling back to the width default on anything else. */
export function parseDockMode(stored: string | null, width: number): ChatDockMode {
  if (stored === "left" || stored === "drawer") return stored;
  return defaultDockMode(width);
}

/** Model the picker should show: the operator's last pick when it is still
 *  offered AND usable, else the deployment's design-agent default. A model
 *  that can't take a forced tool call is never resolved to — the agent's
 *  every turn forces one, so it would fail at the first message. */
export function resolveChatModel(opts: {
  stored: string | null;
  models: Array<{ id: string; forcedToolUse: boolean }>;
  agentModel?: string;
  defaultModel?: string;
}): string {
  const usable = opts.models.filter((m) => m.forcedToolUse);
  const ok = (id: string | null | undefined): boolean =>
    !!id && usable.some((m) => m.id === id);
  if (ok(opts.stored)) return opts.stored!;
  if (ok(opts.agentModel)) return opts.agentModel!;
  if (ok(opts.defaultModel)) return opts.defaultModel!;
  return usable[0]?.id ?? opts.defaultModel ?? "";
}

/** "$12.40" / "-$3.20" — the sign leads, so a negative balance reads as a
 *  debt rather than a weirdly-punctuated number. */
export function formatUsd(usd: number, digits = 2): string {
  if (!Number.isFinite(usd)) return "—";
  const sign = usd < 0 ? "-" : "";
  return `${sign}$${Math.abs(usd).toFixed(digits)}`;
}

/* --------------------------- persistence (guarded) ----------------------- */

function readLs(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null; // private mode / storage disabled
  }
}

function writeLs(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    /* best effort — the dock still works, it just forgets */
  }
}

export function loadDockMode(width: number): ChatDockMode {
  return parseDockMode(readLs(LS_DOCK), width);
}

export function saveDockMode(mode: ChatDockMode): void {
  writeLs(LS_DOCK, mode);
}

export function loadDockCollapsed(width: number): boolean {
  return resolveDockCollapsed(readLs(LS_COLLAPSED), width);
}

export function saveDockCollapsed(
  collapsed: boolean,
  width: number = typeof window === "undefined" ? 0 : window.innerWidth,
): void {
  // A narrow window's open/close is for the visit only — see
  // `resolveDockCollapsed`.
  if (!dockCollapsedIsPersistable(width)) return;
  writeLs(LS_COLLAPSED, collapsed ? "1" : "0");
}

export function loadChatModel(): string | null {
  return readLs(LS_MODEL);
}

export function saveChatModel(model: string): void {
  writeLs(LS_MODEL, model);
}

/** Selected thread is per clip — two clips open in two tabs keep their own. */
export function loadSelectedThread(clipName: string): string | null {
  const v = readLs(LS_THREAD_PREFIX + clipName);
  return v && THREAD_ID_RE.test(v) ? v : null;
}

export function saveSelectedThread(clipName: string, threadId: string): void {
  writeLs(LS_THREAD_PREFIX + clipName, threadId);
}
